import { ALLOWED_UPLOAD_EXTENSIONS, UPLOAD_MAX_BYTES } from '@atlas/api-contract';

import { formatBytes } from '@/lib/format/bytes';

/**
 * What the browser can tell about an archive before sending it.
 *
 * **These are warnings, never gates.** The server is the only authority on what
 * an archive contains, and it applies its limits to bytes it has counted itself
 * — so a pre-check that *blocked* the upload button would be a client-side rule
 * with no way to be right: a file the browser reports as 50.1 MB may be exactly
 * at the limit, `UPLOAD_MAX_BYTES` may have been raised on the server, and a ZIP
 * saved without a `.zip` extension is still a ZIP. Every check here therefore
 * produces a sentence next to the file input and nothing else. The upload
 * proceeds, the server decides, and `upload-errors.ts` renders its answer.
 *
 * The point is to save a two-minute upload that was always going to be refused,
 * not to pre-empt the refusal.
 *
 * Bounds come from `@atlas/api-contract`, so raising a server limit relaxes the
 * warning in the same commit rather than leaving a stale number in the UI.
 */

export type UploadWarningCode = 'not-a-zip' | 'empty-file' | 'too-large';

export interface UploadWarning {
  code: UploadWarningCode;
  /** One sentence, already naming the file and the limit. */
  message: string;
}

/**
 * The two facts a pre-check reads from a picked file.
 *
 * Declared structurally rather than as `File` so the rules can be exercised
 * without a DOM — the test environment is Node.
 */
export interface PickedFile {
  name: string;
  size: number;
}

/** The `accept` attribute for the file input. A hint to the picker, not a rule. */
export const ARCHIVE_ACCEPT = '.zip,application/zip,application/x-zip-compressed';

/** The archive format the unpacker reads. */
const ARCHIVE_EXTENSION = 'zip';

/**
 * Lowercase extension without the dot, or `''` when there is none.
 *
 * A leading dot is a hidden file rather than an extension (`.zip` is a file
 * named `.zip`), which is why the dot has to be past the first character.
 */
export const fileExtension = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');

  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
};

/**
 * Everything questionable about the picked file, in the order it is worth
 * reading. An empty array means nothing looks wrong from here — which is not a
 * promise that the server will accept it.
 */
export const precheckArchive = (file: PickedFile): UploadWarning[] => {
  const warnings: UploadWarning[] = [];

  if (fileExtension(file.name) !== ARCHIVE_EXTENSION) {
    warnings.push({
      code: 'not-a-zip',
      message: `“${file.name}” does not look like a .zip. Atlas unpacks ZIP archives; anything else will be refused.`,
    });
  }

  if (file.size <= 0) {
    warnings.push({
      code: 'empty-file',
      message: `“${file.name}” is empty. An archive must contain an index.html plus its assets.`,
    });
  } else if (file.size > UPLOAD_MAX_BYTES) {
    warnings.push({
      code: 'too-large',
      message: `“${file.name}” is ${formatBytes(file.size)}, over the ${formatBytes(UPLOAD_MAX_BYTES)} upload limit. It will almost certainly be refused.`,
    });
  }

  return warnings;
};

/**
 * The extensions kept when the archive is unpacked, as a readable list.
 *
 * Shown next to the file input because a dropped file is the single most
 * confusing outcome of an otherwise successful upload, and knowing the list
 * beforehand is what turns "where did my .exe go" into "of course it did".
 */
export const ALLOWED_CONTENTS_SUMMARY = ALLOWED_UPLOAD_EXTENSIONS.map(
  (extension) => `.${extension}`,
).join(', ');
